import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { ticketRef } from "utilities/ticketNumber";
import { useNavigate, Link } from "react-router-dom";
import { requireAuth, useAuth } from "utilities/auth";
import { isInternal, isPartner } from "utilities/userType";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { SearchProvider, useSearch } from "context/SearchContext";
import { useTickets, useTicketRequestTypes } from "hooks/crm/useTickets";
import PageHeader from "components/PageLayout";
import PageKpis from "components/PageKpis";
import RelativeTime from "components/RelativeTime";
import UserAvatar from "components/UserAvatar";
import SearchPanel from "components/SearchPanel";
import RecordTable from "components/Records/RecordTable";
import ListSearchBar from "components/Records/ListSearchBar";
import ListPagination from "components/Records/ListPagination";
import { countActiveFilters, filterFieldsFor } from "utilities/listFilters";
import TicketStatusPill from "components/Tickets/TicketStatusPill";
import PriorityIndicator from "components/Tickets/PriorityIndicator";
import TicketCreateModal from "components/Tickets/TicketCreateModal";
import SlaBadge from "components/Tickets/SlaBadge";
import { SLA_BUCKET_OPTIONS } from "utilities/sla";
import { useTicketTemplates } from "hooks/useTicketTemplates";
import { ticketVariantDisplay, variantFilterOptions } from "utilities/ticketVariants";
import {
  Filter,
  Inbox,
  Plus,
  User as UserIcon,
  PauseCircle,
  Anchor,
  Timer,
} from "lucide-react";

const TICKET_FILTER_FIELDS = filterFieldsFor("tickets");

// "all" is an explicit value, not "" — see the `state` field in searchConfig:
// empty values are stripped from the URL and fall back to the default ("open"),
// so an empty "All" tab could never actually be selected.
const STATE_TABS = [
  { value: "all", label: "All" },
  { value: "open", label: "Open" },
  { value: "closed", label: "Closed" },
];

function humanize(key) {
  if (!key) return "";
  return String(key)
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function companyNameOf(t) {
  return t.companyName || t.company?.name || "";
}
// SOUND-611: the MID descriptor is DISPLAY ONLY (it's mutable — key on midId).
function midLabelOf(t) {
  return t.midLabel || "";
}function assigneeOf(t) {
  if (!t.assignee) return null;
  return {
    name: t.assignee.displayName || t.assignee.email,
    email: t.assignee.email,
  };
}

function TicketsInner() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const internal = isInternal(user);
  const partner = isPartner(user);
  const companyId = useEffectiveCompanyId();
  const { filters, set, setAll } = useSearch();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  // SLA view controls (SOUND-535) — now SERVER-side + cross-page. `slaBucket`
  // and `sort` are persisted filters (SearchProvider); `sort=sla` gives the
  // breached-first, soonest-due server ordering. No more loaded-page sorting.
  const slaFilter = filters.slaBucket || "";
  // One sort, one home. Column headers and the SLA control both write
  // filters.sort, so they cannot disagree — "sla" is the breached-first server
  // ordering, anything else is a "-field" column sort. Keeping a separate piece
  // of component state for column sorting would let the dropdown say "Recent"
  // while the table was ordered by #number.
  const sortMode = filters.sort === "sla" ? "sla" : "recent";
  const sortParam = filters.sort || "-updatedAt";
  const columnSort = filters.sort === "sla" ? "" : sortParam;

  const { tickets, pagination, loading, error, refetch } = useTickets({
    filters,
    page,
    limit,
    sort: sortParam,
    // Dependency enrichment is an internal triage aid only (SOUND-499).
    include: internal ? "deps" : undefined,
  });
  const { requestTypes } = useTicketRequestTypes();
  const rtLabel = useMemo(() => {
    const map = {};
    requestTypes.forEach((rt) => (map[rt.key] = rt.label));
    return (key) => map[key] || humanize(key);
  }, [requestTypes]);
  // SOUND-785 — workflow variants, for the Variant filter and the variant shown
  // under a row's request type. Templates are internal-only, so this is too.
  const { templates } = useTicketTemplates({}, { enabled: internal });
  const variantOf = useCallback(
    (t) => (internal ? ticketVariantDisplay(t, templates, rtLabel(t.requestType)) : null),
    [internal, templates, rtLabel]
  );
  // Feeds the Request Type and Variant filters' options. Memoised so
  // SearchPanel's own optionsMap useMemo isn't invalidated on every render by
  // a fresh literal. Variants narrow to the filtered request type when one is
  // applied (a variantKey is only unique within a type).
  const filterOptions = useMemo(
    () => ({
      requestTypeOptions: requestTypes,
      variantOptions: variantFilterOptions(templates, { requestTypeFilter: filters.requestType, rtLabel }),
    }),
    [requestTypes, templates, filters.requestType, rtLabel]
  );

  // Reset to page 1 whenever the applied filters change.
  const prevFiltersRef = useRef(filters);
  useEffect(() => {
    if (prevFiltersRef.current !== filters) {
      setPage(1);
      prevFiltersRef.current = filters;
    }
  }, [filters]);

  // ---- KPI strip (internal only) — inbox-wide counts, filter-independent ----
  const [kpi, setKpi] = useState({
    open: 0,
    waiting: 0,
    unassigned: 0,
    resolved: 0,
    loading: true,
  });
  const [kpiNonce, setKpiNonce] = useState(0);

  const loadKpis = useCallback(async () => {
    if (!internal || !companyId || !user) return;
    setKpi((k) => ({ ...k, loading: true }));
    const authUser = { id: user.id, accessToken: user.accessToken };
    try {
      // SOUND-494: one server-side aggregate replaces the 4 per-count list
      // calls. `resolved` is now "resolved this week" (exact), not all-time.
      const res = await makeApiRequest("get", apiRoutes.ticketStats, authUser, companyId);
      const s = res.data?.data || {};
      setKpi({
        open: s.open ?? 0,
        waiting: s.waiting ?? 0,
        resolved: s.resolvedThisWeek ?? 0,
        unassigned: s.unassigned ?? 0,
        loading: false,
      });
    } catch {
      setKpi((k) => ({ ...k, loading: false }));
    }
  }, [internal, companyId, user]);

  useEffect(() => {
    loadKpis();
  }, [loadKpis, kpiNonce]);

  const meFilterOn = internal && filters.assigneeId === user?.id;
  // SOUND-501 — Unassigned queue. Mutually exclusive with "Assigned to me"
  // (a ticket can't be both mine and unassigned), so each toggle clears the
  // other in one atomic setAll write.
  const unassignedOn = internal && filters.assigned === "none";

  // `state` defaults to "open" and `sort`/`assigned` are view controls, not
  // filters — the hand-written version had to encode both exceptions inline.
  // countActiveFilters reads the declared defaults from searchConfig instead.
  const activeFilterCount = countActiveFilters(filters, TICKET_FILTER_FIELDS, {
    ignore: ["sort", "assigned"],
  });
  const hasActiveFilters = activeFilterCount > 0;

  // Column model. Sortable only where the SERVER can order: `priority` is stored
  // as a string, so Mongo sorts it alphabetically (high < normal < urgent) and
  // inverts the severity; `companyName` is enriched onto the response after the
  // query (0 of 524 tickets store it). Neither carries a sortKey.
  const columns = useMemo(
    () => [
      {
        key: "number",
        header: "#",
        // SC-524 is six characters of monospace where #44 was three — 5% was
        // sized for the old bare number and truncated the new one to "SC…".
        width: "8%",
        sortKey: "number",
        defaultSortDir: "desc",
        exportValue: (t) => t.number,
        // SOUND-499: a load-bearing ticket (one that blocks others) gets a brand
        // rail at the row's left edge. It belongs on the CELL, not on an inner
        // span — inside the cell it reads as an indent on the number rather than
        // a marker on the row, which is what it looked like after the port.
        cellClassName: (t) =>
          internal && (t.blocks || []).length ? "border-l-2 border-l-brand" : "",
        className: "font-mono text-[11px] tabular-nums text-muted-foreground",
        cell: (t) => <>{ticketRef(t.number)}</>,
      },
      internal && {
        key: "priority",
        header: "Priority",
        width: "8%",
        exportValue: (t) => t.priority || "",
        cell: (t) => <PriorityIndicator priority={t.priority} />,
      },
      // SOUND-583/611: internal ops are the primary users of this inbox and had
      // NO client column — the top-reported gap. Clients only ever see their own
      // tickets, so it stays hidden for them.
      (internal || partner) && {
        key: "client",
        header: "Client",
        width: "16%",
        exportValue: (t) => companyNameOf(t),
        cell: (t) => (
          <>
            <div className="truncate">{companyNameOf(t) || "—"}</div>
            {/* MID under the client groups "who + which account" in one
                scannable cell (SOUND-581). */}
            {midLabelOf(t) && (
              <div className="truncate font-mono text-[11px] text-muted-foreground">
                {midLabelOf(t)}
              </div>
            )}
          </>
        ),
      },
      {
        key: "title",
        header: "Title",
        // Widest by intent: it is what the row is actually about, and with
        // table-fixed an undeclared width means "whatever is left over".
        width: "35%",
        sortKey: "title",
        // The deps popover is positioned outside its cell, so this column must
        // not clip — see RecordTable's truncate opt-out.
        truncate: false,
        exportValue: (t) => t.title || "",
        cell: (t, idx, total) => {
          const blocks = internal ? t.blocks || [] : [];
          const blockedByOpen = internal ? t.blockedByOpen || [] : [];
          const isBlocked = blockedByOpen.length > 0;
          const variant = variantOf(t);
          const secondLine = t.requestType
            ? `${rtLabel(t.requestType)}${variant ? ` · ${variant.label}` : ""}`
            : "";
          return (
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span
                  className={`truncate ${isBlocked ? "text-muted-foreground" : "font-medium"}`}
                >
                  {t.title}
                </span>
                {t.kind === "task" && <span className="badge badge-muted shrink-0">Task</span>}
                {internal && (
                  <RowDepsMarker
                    blocks={blocks}
                    blockedByOpen={blockedByOpen}
                    // Open upward for the last rows so the scroll container's
                    // edge cannot clip the popover.
                    openUp={idx >= total - 2}
                  />
                )}
              </div>
              {secondLine && (
                <div className="truncate text-[11px] text-muted-foreground">{secondLine}</div>
              )}
            </div>
          );
        },
      },
      {
        key: "status",
        header: "Status",
        width: "11%",
        sortKey: "status",
        exportValue: (t) => t.status || "",
        cell: (t) => <TicketStatusPill status={t.status} statusCategory={t.statusCategory} />,
      },
      internal && {
        key: "sla",
        header: "SLA",
        width: "8%",
        exportValue: (t) => t.sla?.dueAt || "",
        cell: (t) =>
          t.sla && t.sla.policyId ? (
            <SlaBadge ticket={t} />
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      internal && {
        key: "assignee",
        header: "Assignee",
        // Avatar only. The name is on hover (UserAvatar sets title +
        // aria-label), which buys the width back for Title — in a queue you
        // scan initials to find your own rows, you do not read the names.
        width: "6%",
        headerClassName: "hidden lg:table-cell",
        className: "hidden lg:table-cell",
        exportValue: (t) => assigneeOf(t)?.name || "",
        cell: (t) => {
          const assignee = assigneeOf(t);
          return assignee ? (
            <UserAvatar
              user={{ name: assignee.name, email: assignee.email }}
              size={22}
            />
          ) : (
            <span className="text-muted-foreground" title="Unassigned">
              —
            </span>
          );
        },
      },
      {
        key: "updatedAt",
        header: "Updated",
        width: "8%",
        sortKey: "updatedAt",
        defaultSortDir: "desc",
        className: "text-muted-foreground",
        exportValue: (t) => t.updatedAt || "",
        cell: (t) => <RelativeTime value={t.updatedAt} />,
      },
    ],
    [internal, partner, rtLabel, variantOf]
  );

  const title = internal ? "Tickets" : "Support";
  const subtitle = internal
    ? "Merchant-services operations — every client's requests in one inbox."
    : partner
    ? "Requests across your attributed clients."
    : "Your requests to the Sound team — open one to track its status.";

  return (
    <div>
      <PageHeader
        title={title}
        subtitle={subtitle}
        meta={[{ label: "Total", value: (pagination.total || 0).toLocaleString() }]}
        actions={
          <div className="flex items-center gap-2">
            {internal && (
              <button className="btn btn-secondary" onClick={() => setFiltersOpen(true)}>
                <Filter className="h-4 w-4" />
                Filters
              </button>
            )}
            <button className="btn btn-primary" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              {internal ? "New ticket" : "New request"}
            </button>
          </div>
        }
      />

      <div className="container space-y-6 py-6">
        {/* KPI strip — internal only */}
        {internal && (
          <PageKpis
            items={[
              { label: "Open", value: kpi.open, tone: "info", loading: kpi.loading, icon: <Inbox /> },
              { label: "Waiting on client", value: kpi.waiting, tone: "warning", loading: kpi.loading, icon: <Filter /> },
              { label: "Unassigned", value: kpi.unassigned, tone: "destructive", loading: kpi.loading, icon: <UserIcon />,
                // SOUND-501: one-click into the open-unassigned queue (matches this KPI's count).
                onClick: () => setAll({ assigned: "none", state: "open", assigneeId: "" }) },
              { label: "Resolved", value: kpi.resolved, tone: "success", loading: kpi.loading, icon: <Inbox /> },
            ]}
          />
        )}

        <ListSearchBar
          value={filters.search || ""}
          onChange={(v) => set("search", v)}
          placeholder={
            internal || partner
              ? "Search client, title, description, or #number"
              : "Search title, description, or #number"
          }
        >
          {/* State segmented control */}
          <div className="inline-flex overflow-hidden rounded-md border border-border">
            {STATE_TABS.map((s) => {
              const active = (filters.state || "open") === s.value;
              return (
                <button
                  key={s.value || "all"}
                  type="button"
                  onClick={() => set("state", s.value)}
                  className={`px-3 py-2 text-xs font-semibold transition-colors ${
                    active
                      ? "bg-brand/[0.14] text-foreground"
                      : "bg-card text-muted-foreground hover:text-foreground"
                  } ${s.value ? "border-l border-border" : ""}`}
                >
                  {s.label}
                </button>
              );
            })}
          </div>

          {/* Internal-only quick selects */}
          {internal && (
            <>
              {/* Status dropdown hidden from the inline bar (CPO 2026-07-28);
                  still filterable via the advanced-filters drawer. */}
              <select
                className="select w-auto"
                value={filters.priority || ""}
                onChange={(e) => set("priority", e.target.value)}
                aria-label="Priority"
              >
                <option value="">Priority: Any</option>
                {["urgent", "high", "normal", "low"].map((p) => (
                  <option key={p} value={p}>
                    {humanize(p)}
                  </option>
                ))}
              </select>
              {/* SLA quick filter + sort — SERVER-side, cross-page (SOUND-535) */}
              <div className="relative">
                <Timer className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <select
                  className="select w-auto pl-8"
                  value={slaFilter}
                  onChange={(e) => set("slaBucket", e.target.value)}
                  aria-label="SLA"
                >
                  <option value="">SLA: Any</option>
                  {SLA_BUCKET_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <select
                className="select w-auto"
                value={sortMode}
                onChange={(e) => set("sort", e.target.value === "sla" ? "sla" : "")}
                aria-label="Sort"
              >
                <option value="recent">Sort: Recent</option>
                <option value="sla">Sort: SLA (breached first)</option>
              </select>
              {/* Assignment toggles — moved to the end of the row (CPO 2026-07-28) */}
              <button
                type="button"
                onClick={() => setAll({ assigneeId: meFilterOn ? "" : user?.id, assigned: "" })}
                className={`btn ${meFilterOn ? "btn-secondary" : "btn-outline"}`}
              >
                <UserIcon className="h-4 w-4" />
                Assigned to me
              </button>
              {/* SOUND-501 — Unassigned queue toggle (clears the me-filter). */}
              <button
                type="button"
                onClick={() => setAll({ assigned: unassignedOn ? "" : "none", assigneeId: "" })}
                className={`btn ${unassignedOn ? "btn-secondary" : "btn-outline"}`}
              >
                <UserIcon className="h-4 w-4" />
                Unassigned
              </button>
            </>
          )}
        </ListSearchBar>

        <RecordTable
          columns={columns}
          rows={tickets}
          rowKey={(t) => t.ticketId}
          // SOUND-499 triage: a blocked ticket recedes — "don't pick this up".
          rowClassName={(t) =>
            internal && (t.blockedByOpen || []).length ? "bg-muted/30" : ""
          }
          onRowClick={(t) => navigate(`/tickets/${t.ticketId}`)}
          loading={loading}
          error={error}
          onRetry={refetch}
          filtered={hasActiveFilters}
          sort={columnSort}
          onSortChange={(next) => {
            set("sort", next);
            setPage(1);
          }}
          noMatch={{ icon: Inbox, heading: "No matching tickets" }}
          empty={{
            icon: Inbox,
            heading: internal
              ? "No tickets in the queue"
              : "You haven't opened any requests yet",
            description: internal
              ? "New client requests will land here."
              : "Open a request and we'll get right on it.",
            errorText: "Failed to load tickets.",
            action: (
              <button className="btn btn-primary" onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4" />
                {internal ? "New ticket" : "New request"}
              </button>
            ),
          }}
        />

        <ListPagination
          page={pagination.page || page}
          totalPages={pagination.totalPages || 0}
          total={pagination.total || 0}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      </div>

      {internal && (
        <SearchPanel
          namespace="tickets"
          title="Filter tickets"
          isExpanded={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          hiddenFields={["assigneeId", "slaBucket", "sort", "assigned"]}
          extraOptions={filterOptions}
        />
      )}

      <TicketCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(ticket) => {
          setCreateOpen(false);
          setKpiNonce((n) => n + 1);
          if (ticket?.ticketId) navigate(`/tickets/${ticket.ticketId}`);
          else refetch();
        }}
      />
    </div>
  );
}

/**
 * RowDepsMarker (SOUND-499) — internal inbox dependency indicator.
 * -------------------------------------------------------
 * Accessible, not-color-only markers for a row's open dependencies:
 *   - load-bearing (blocks others) → Anchor glyph (brand)
 *   - blocked (has open blockers)  → PauseCircle glyph (muted) "don't pick up"
 * A `title` gives the plain-text list for keyboard/SR (always works, never
 * clips); a DOM-sibling hover/focus popover lists each dependency deep-linking
 * to /tickets/:id. The popover is a descendant of the marker wrapper so the
 * row's onClick is stopped and focus-within keeps the links tab-reachable.
 */
function RowDepsMarker({ blocks = [], blockedByOpen = [], openUp = false }) {
  const isBlocked = blockedByOpen.length > 0;
  const isLoadBearing = blocks.length > 0;
  if (!isBlocked && !isLoadBearing) return null;

  const titleParts = [];
  if (isLoadBearing) titleParts.push(`Blocks ${blocks.map((b) => ticketRef(b.number)).join(", ")}`);
  if (isBlocked) titleParts.push(`Blocked by ${blockedByOpen.map((b) => ticketRef(b.number)).join(", ")}`);
  const title = titleParts.join(" · ");

  const stop = (e) => e.stopPropagation();
  const stopKeys = (e) => {
    if (e.key === "Enter" || e.key === " ") e.stopPropagation();
  };

  const linkRow = (items, cls) => (
    <div className="flex flex-wrap gap-x-2 gap-y-1">
      {items.map((b) => (
        <Link
          key={b.ticketId}
          to={`/tickets/${b.ticketId}`}
          onClick={stop}
          className={`font-mono text-xs hover:underline ${cls}`}
        >
          {ticketRef(b.number)}
        </Link>
      ))}
    </div>
  );

  return (
    <span className="group/deps relative inline-flex items-center" onClick={stop} onKeyDown={stopKeys}>
      <span
        tabIndex={0}
        role="button"
        aria-label={title}
        title={title}
        className="inline-flex items-center gap-1 rounded outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
      >
        {isLoadBearing && <Anchor className="h-3.5 w-3.5 text-brand" aria-hidden="true" />}
        {isBlocked && <PauseCircle className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />}
      </span>
      <div
        className={`absolute left-0 z-30 hidden group-hover/deps:block group-focus-within/deps:block ${
          openUp ? "bottom-full pb-1.5" : "top-full pt-1.5"
        }`}
      >
        <div className="min-w-[180px] rounded-md border border-border bg-card p-2.5 text-left shadow-lg">
          {isLoadBearing && (
            <div>
              <div className="mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <Anchor className="h-3 w-3 text-brand" />
                Blocks
              </div>
              {linkRow(blocks, "text-brand")}
            </div>
          )}
          {isBlocked && (
            <div className={isLoadBearing ? "mt-2 border-t border-border/60 pt-2" : ""}>
              <div className="mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <PauseCircle className="h-3 w-3" />
                Blocked by
              </div>
              {linkRow(blockedByOpen, "text-foreground hover:text-brand")}
            </div>
          )}
        </div>
      </div>
    </span>
  );
}


function Tickets() {
  return (
    <SearchProvider namespace="tickets">
      <TicketsInner />
    </SearchProvider>
  );
}

export default requireAuth(Tickets);
